import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import hepatoApi, { HepatoForm, HepatoFormStatus, UpdateHepatoFormDto } from '../../../api/hepatoApi';
import { Patient, patientApi } from '../../../api/patientApi';
import { useFlowState } from '../../../components/flow/FlowPanel';
import type { FlowState } from '../../../api/flowApi';

export type StepSaveHandler = () => Promise<boolean>;

interface HepatoWizardValue {
  patient: Patient | null;
  form: HepatoForm | null;
  loading: boolean;
  error: string | null;
  flow: FlowState | null;
  reloadFlow: () => Promise<void>;
  saveForm: (patch: UpdateHepatoFormDto) => Promise<HepatoForm | null>;
  submit: () => Promise<boolean>;
  registerStepSave: (fn: StepSaveHandler | null) => void;
  runStepSave: () => Promise<boolean>;
}

const Ctx = createContext<HepatoWizardValue | null>(null);

export const useHepatoWizard = (): HepatoWizardValue => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useHepatoWizard must be used inside HepatoWizardProvider');
  return v;
};

/** Înregistrează handler-ul de salvare al pasului curent (folosit de „Salvează și continuă"). */
export const useRegisterHepatoStepSave = (handler: StepSaveHandler): void => {
  const { registerStepSave } = useHepatoWizard();
  useEffect(() => {
    registerStepSave(handler);
    return () => registerStepSave(null);
  });
};

interface ProviderProps {
  patientId: string;
  editFormId?: string | null;
  children: ReactNode;
}

export const HepatoWizardProvider = ({ patientId, editFormId, children }: ProviderProps) => {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [form, setForm] = useState<HepatoForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const stepSaveRef = useRef<StepSaveHandler | null>(null);
  const flow = useFlowState('hepato', form?.id);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        setLoading(true);
        setError(null);
        const p = await patientApi.getById(patientId);
        if (cancelled) return;
        setPatient(p);

        let f: HepatoForm;
        if (editFormId) {
          f = await hepatoApi.getById(editFormId);
        } else {
          const drafts = await hepatoApi.list({
            patientId: p.id,
            status: HepatoFormStatus.DRAFT,
            limit: 1,
            sortBy: 'updatedAt',
            sortOrder: 'desc',
          });
          f = drafts.data[0] ?? (await hepatoApi.create({ patientId: p.id, programType: 'HEPATO' }));
        }
        if (cancelled) return;
        setForm(f);
      } catch (e: any) {
        setError(e?.response?.data?.message ?? e?.message ?? 'Eroare la inițializarea wizard-ului');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [patientId, editFormId]);

  const saveForm = useCallback(
    async (patch: UpdateHepatoFormDto) => {
      if (!form) return null;
      try {
        const updated = await hepatoApi.update(form.id, patch);
        setForm(updated);
        void flow.reload();
        return updated;
      } catch (e) {
        console.error('Eroare la salvarea formularului', e); // toast: interceptorul axios
        return null;
      }
    },
    [form, flow]
  );

  const submit = useCallback(async () => {
    if (!form) return false;
    try {
      setForm(await hepatoApi.submit(form.id));
      message.success('Formular trimis spre revizuire');
      return true;
    } catch (e) {
      console.error('Eroare la trimiterea formularului', e);
      return false;
    }
  }, [form]);

  const registerStepSave = useCallback((fn: StepSaveHandler | null) => {
    stepSaveRef.current = fn;
  }, []);

  const runStepSave = useCallback(async () => {
    const fn = stepSaveRef.current;
    if (!fn) return true;
    try {
      return await fn();
    } catch (e) {
      console.error('Eroare la salvarea pasului curent', e);
      return false;
    }
  }, []);

  const value = useMemo<HepatoWizardValue>(
    () => ({
      patient,
      form,
      loading,
      error,
      flow: flow.state,
      reloadFlow: flow.reload,
      saveForm,
      submit,
      registerStepSave,
      runStepSave,
    }),
    [patient, form, loading, error, flow.state, flow.reload, saveForm, submit, registerStepSave, runStepSave]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
